import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Checkbox,
  ListItemIcon,
} from '@mui/material';
import { getFiscalDevice } from '../../hooks/fiscalDevices';

// ✅ Ödeme geçmişi için interface (PartialPaymentModal'dan kopyalandı)
export interface PaymentHistoryItem {
  id: string;
  type: 'cash' | 'pos' | 'open_account';
  label: string;
  amount: number;
  timestamp: number;
  posId?: string;
  status: 'pending' | 'paid' | 'refunded';
  fiscalData?: any;
  paymentSequenceNo?: number;
}

// ✅ YENİ: LocalStorage'dan bekleyen POS iadelerini kontrol et (export ediliyor)
export const hasPendingPosRefundsInStorage = (companyId: string | null): boolean => {
  if (!companyId) return false;

  try {
    const key = `partialRefunds_${companyId}`;
    const existing = localStorage.getItem(key);
    if (!existing) return false;
    
    const refunds: PaymentHistoryItem[] = JSON.parse(existing);
    const posRefunds = refunds.filter(r => r.type === 'pos' && r.status === 'refunded');
    
    return posRefunds.length > 0;
  } catch (e) {
    console.error('[PosRefundModal] Kontrol hatası:', e);
    return false;
  }
};

interface PosRefundModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  selectedFiscalDevice: 'hugin_t300' | 'inpos_m530' | 'beko_x30tr' | 'beko_300tr' | 'pavopay' | 'ingenico_5000f';
  showAlertModal: (title: string, message: string) => void;
}

const PosRefundModal: React.FC<PosRefundModalProps> = ({
  open,
  onClose,
  companyId,
  selectedFiscalDevice,
  showAlertModal,
}) => {
  const [partialRefunds, setPartialRefunds] = useState<PaymentHistoryItem[]>([]);
  const [selectedRefundIds, setSelectedRefundIds] = useState<Set<string>>(new Set()); // ✅ YENİ: Seçilen iade ID'leri
  const [currentRefundIndex, setCurrentRefundIndex] = useState(0);
  const [refundResults, setRefundResults] = useState<{ success: boolean; message: string }[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStarted, setIsStarted] = useState(false); // ✅ YENİ: İade işlemi başlatıldı mı?

  // ✅ LocalStorage'dan kısmi iadeleri yükle
  useEffect(() => {
    if (open && companyId) {
      loadPartialRefunds();
    }
  }, [open, companyId]);

  const loadPartialRefunds = () => {
    if (!companyId) return;

    try {
      const key = `partialRefunds_${companyId}`;
      const existing = localStorage.getItem(key);
      const refunds: PaymentHistoryItem[] = existing ? JSON.parse(existing) : [];
      
      // Sadece POS ödemelerini filtrele
      const posRefunds = refunds.filter(r => r.type === 'pos' && r.status === 'refunded');
      
      setPartialRefunds(posRefunds);
      setCurrentRefundIndex(0);
      setRefundResults([]);
      setIsStarted(false); // ✅ İade başlatılmadı
      
      // ✅ Tüm ödemeleri varsayılan olarak seçili yap
      setSelectedRefundIds(new Set(posRefunds.map(r => r.id)));
      
      console.log('[PosRefundModal] Kısmi iadeler yüklendi:', posRefunds.length);
    } catch (e) {
      console.error('[PosRefundModal] Yükleme hatası:', e);
    }
  };

  // ✅ LocalStorage'dan kısmi iade kaydını sil
  const removePartialRefundFromStorage = (paymentId: string) => {
    if (!companyId) return;

    try {
      const key = `partialRefunds_${companyId}`;
      const existing = localStorage.getItem(key);
      if (!existing) return;
      
      const refunds: PaymentHistoryItem[] = JSON.parse(existing);
      const filtered = refunds.filter(r => r.id !== paymentId);
      
      localStorage.setItem(key, JSON.stringify(filtered));
      console.log('[PosRefundModal] Kısmi iade silindi:', paymentId);
    } catch (e) {
      console.error('[PosRefundModal] Silme hatası:', e);
    }
  };

  // ✅ YENİ: Checkbox toggle
  const handleToggleSelect = (paymentId: string) => {
    if (isStarted) return; // İade başladıysa seçim değiştirilemez
    
    setSelectedRefundIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(paymentId)) {
        newSet.delete(paymentId);
      } else {
        newSet.add(paymentId);
      }
      return newSet;
    });
  };

  // ✅ YENİ: Tümünü seç/kaldır
  const handleToggleAll = () => {
    if (isStarted) return; // İade başladıysa seçim değiştirilemez
    
    if (selectedRefundIds.size === partialRefunds.length) {
      setSelectedRefundIds(new Set());
    } else {
      setSelectedRefundIds(new Set(partialRefunds.map(r => r.id)));
    }
  };

  // ✅ Gerçek iade işlemi
  const handleProcessRealRefund = async (payment: PaymentHistoryItem) => {
    try {
      console.log(`[PosRefundModal] 💳 Gerçek iade yapılıyor: ${payment.label} - ${payment.amount} TL`);
      
      const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
      
      if (!fiscalDevice.refundPosPaymentAfterClose) {
        throw new Error('Gerçek POS iadesi desteklenmiyor');
      }
      
      // ✅ Belge kapandı, RefundEFTPayment kullanabiliriz!
      const refundResult = await fiscalDevice.refundPosPaymentAfterClose(
        payment.amount,
        payment.fiscalData?.AcquirerId,
        showAlertModal
      );
      
      if (refundResult.success) {
        console.log(`[PosRefundModal] ✅ ${payment.label} başarıyla iade edildi`);
        return {
          success: true,
          message: `${payment.label} için ${payment.amount} TL başarıyla iade edildi! Kart sahibine iade slip'i verilecek.`
        };
      } else {
        console.error(`[PosRefundModal] ❌ ${payment.label} iade edilemedi:`, refundResult.errorMessage);
        return {
          success: false,
          message: `${payment.label} için ${payment.amount} TL iade edilemedi.\n\nHata: ${refundResult.errorMessage}\n\nLÜTFEN POS TERMİNALİNDEN MANUEL İADE YAPIN:\nAuth Code: ${payment.fiscalData?.AuthCode || 'Bilinmiyor'}`
        };
      }
    } catch (error: any) {
      console.error(`[PosRefundModal] ❌ ${payment.label} iade hatası:`, error);
      return {
        success: false,
        message: `${payment.label} için ${payment.amount} TL iade edilirken hata oluştu.\n\nLÜTFEN POS TERMİNALİNDEN MANUEL İADE YAPIN:\nAuth Code: ${payment.fiscalData?.AuthCode || 'Bilinmiyor'}`
      };
    }
  };

  // ✅ Sıradaki ödemeyi iade et (Sadece seçilenleri)
  const handleRefundNext = async () => {
    // ✅ İlk çağrıda iade işlemini başlat
    if (!isStarted) {
      setIsStarted(true);
      setCurrentRefundIndex(0);
      setRefundResults([]);
    }
    
    // Seçili ödemeleri filtrele
    const selectedPayments = partialRefunds.filter(p => selectedRefundIds.has(p.id));
    
    if (currentRefundIndex >= selectedPayments.length) {
      // Tüm iadeler tamamlandı
      console.log('[PosRefundModal] ✅ Tüm iadeler tamamlandı');
      
      // Başarı modalı göster
      const successCount = refundResults.filter(r => r.success).length;
      const failCount = refundResults.filter(r => !r.success).length;
      
      if (failCount === 0) {
        showAlertModal('✅ İadeler Tamamlandı', `${successCount} adet POS ödemesi başarıyla iade edildi!`);
      } else {
        showAlertModal('⚠️ İadeler Tamamlandı', `${successCount} başarılı, ${failCount} başarısız iade. Manuel iade gerekebilir.`);
      }
      
      // Modalı kapat
      onClose();
      
      // Listeyi yenile
      loadPartialRefunds();
      
      return;
    }
    
    setIsProcessing(true);
    
    // Şu anki ödemeyi iade et
    const currentPayment = selectedPayments[currentRefundIndex];
    const result = await handleProcessRealRefund(currentPayment);
    
    // Sonucu kaydet
    setRefundResults(prev => [...prev, result]);
    
    // ✅ Başarılı iade ise localStorage'dan sil
    if (result.success) {
      removePartialRefundFromStorage(currentPayment.id);
    }
    
    setIsProcessing(false);
    
    // Sıradaki ödemeye geç
    setCurrentRefundIndex(prev => prev + 1);
  };

  // ✅ İadeyi atla
  const handleSkip = () => {
    console.log('[PosRefundModal] ⏭️ İade atlandı');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
        💳 Kısmi POS İadeleri
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {partialRefunds.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              İade edilecek kısmi POS ödemesi yok.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1">
                <strong>{partialRefunds.length} adet kısmi POS ödemesi</strong> var. Bu ödemeler sadece belgeden silindi, karttan gerçek iade yapılmadı.
              </Typography>
              
              {/* ✅ YENİ: Tümünü Seç/Kaldır Butonu */}
              {!isStarted && (
                <Button 
                  size="small" 
                  onClick={handleToggleAll}
                  variant="outlined"
                >
                  {selectedRefundIds.size === partialRefunds.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                </Button>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* İade Listesi */}
            <Typography variant="h6" sx={{ mb: 1 }}>
              İade Edilecek Ödemeler: ({selectedRefundIds.size} seçili)
            </Typography>
            <List>
              {partialRefunds.map((payment, index) => {
                // ✅ YENİ: Seçili ödemelerdeki index
                const selectedPayments = partialRefunds.filter(p => selectedRefundIds.has(p.id));
                const selectedIndex = selectedPayments.findIndex(p => p.id === payment.id);
                const isCompleted = isStarted && selectedIndex !== -1 && selectedIndex < currentRefundIndex;
                const isCurrent = isStarted && selectedIndex === currentRefundIndex;
                const result = isCompleted ? refundResults[selectedIndex] : null;
                const isSelected = selectedRefundIds.has(payment.id);
                
                return (
                  <ListItem
                    key={payment.id}
                    sx={{
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: isCompleted 
                        ? (result?.success ? '#e8f5e9' : '#ffebee')
                        : isCurrent
                        ? '#fff3e0'
                        : isSelected
                        ? '#f5f5f5'
                        : '#fff',
                      opacity: !isSelected && !isStarted ? 0.6 : 1,
                    }}
                  >
                    {/* ✅ YENİ: Checkbox */}
                    {!isStarted && (
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(payment.id)}
                          disabled={isStarted}
                        />
                      </ListItemIcon>
                    )}
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isCompleted ? (
                            result?.success ? (
                              <Chip label="✅ İade Edildi" color="success" size="small" />
                            ) : (
                              <Chip label="❌ Başarısız" color="error" size="small" />
                            )
                          ) : isCurrent ? (
                            <Chip label="🔄 İşleniyor..." color="warning" size="small" />
                          ) : isSelected ? (
                            <Chip label="⏳ Seçildi" color="primary" size="small" />
                          ) : (
                            <Chip label="○ Seçilmedi" color="default" size="small" />
                          )}
                          <Typography variant="body1" fontWeight="bold">
                            {payment.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {payment.amount} TL
                          </Typography>
                        </Box>
                      }
                      secondary={
                        result && !result.success ? (
                          <Typography variant="caption" color="error">
                            {result.message}
                          </Typography>
                        ) : isCompleted && result?.success ? (
                          <Typography variant="caption" color="success.main">
                            {result.message}
                          </Typography>
                        ) : isCurrent ? (
                          <Typography variant="caption" color="warning.main">
                            Auth Code: {payment.fiscalData?.AuthCode || 'Bilinmiyor'}
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
            
            {/* İlerleme Göstergesi */}
            {isStarted && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  İlerleme: {currentRefundIndex} / {selectedRefundIds.size}
                </Typography>
                <Box sx={{ flexGrow: 1, height: 8, bgcolor: '#eee', borderRadius: 1, overflow: 'hidden' }}>
                  <Box 
                    sx={{ 
                      width: `${(currentRefundIndex / selectedRefundIds.size) * 100}%`, 
                      height: '100%', 
                      bgcolor: 'primary.main',
                      transition: 'width 0.3s ease'
                    }} 
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={handleSkip}
          color="inherit"
          disabled={isProcessing || (isStarted && selectedRefundIds.size > 0 && currentRefundIndex < selectedRefundIds.size)}
        >
          {partialRefunds.length === 0 ? 'Kapat' : '⏭️ İadeyi Atla (Manuel)'}
        </Button>
        
        {partialRefunds.length > 0 && (
          <Button 
            onClick={handleRefundNext}
            variant="contained"
            color="primary"
            disabled={isProcessing || selectedRefundIds.size === 0 || (isStarted && currentRefundIndex >= selectedRefundIds.size)}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                İşleniyor...
              </>
            ) : isStarted && currentRefundIndex >= selectedRefundIds.size ? (
              '✅ Tüm İadeler Tamamlandı'
            ) : !isStarted ? (
              `💳 İade İşlemini Başlat (${selectedRefundIds.size} ödeme)`
            ) : (
              '➡️ Sıradaki İadeye Geç'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PosRefundModal;

